import { GraduationCap, Building2 } from "lucide-react";
import { SIX_LEVELS, EVIDENCE_STUDENT, EVIDENCE_SCHOOL, IMAGES } from "../data/content";

function Overline({ children, light }) {
  return (
    <p className={`text-xs font-bold uppercase tracking-[0.25em] ${light ? "text-gold" : "text-gold-700"}`}>{children}</p>
  );
}

export default function About() {
  return (
    <main data-testid="about-page">
      {/* Who we are */}
      <section className="bg-ivory" aria-label="Who we are">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <Overline>About</Overline>
            <h1 className="mt-3 font-serif text-4xl font-bold tracking-tight text-navy sm:text-5xl" data-testid="about-heading">
              Who We Are
            </h1>
            <p className="mt-6 text-base leading-relaxed text-slate-700 sm:text-lg" data-testid="about-intro">
              The Experiential Education Foundation is a registered non-profit educational foundation advancing
              curriculum-aligned experiential learning. Guided by an advisory board of retired civil servants,
              distinguished academicians, social thinkers and master educators, we share a singular vision: education is
              not confined to classrooms, and the best lessons happen when students stop observing the world and start
              participating in it.
            </p>
            <p className="mt-4 text-base leading-relaxed text-slate-700 sm:text-lg">
              Through CASIA (Centre for Applied Studies &amp; Immersive Academics), our flagship school-facing
              initiative, we co-design, deliver, document, and evaluate field-based Curriculum Journeys. We act as an
              extension of partner schools across IB, Cambridge, ICSE, ISC, and CBSE frameworks, transforming world
              environments into active classrooms anchored by rigorous inquiry, reflection, and real-world application.
            </p>
          </div>
          <div className="mt-12 flex flex-wrap items-stretch gap-6">
            <div className="flex items-center rounded-lg bg-navy px-6 py-5 shadow-sm">
              <img src="/assets/eef-logo.webp" alt="The Experiential Education Foundation logo" className="h-14 w-auto" data-testid="about-foundation-logo" />
            </div>
            <div className="flex items-center rounded-lg border border-slate-200 bg-white px-6 py-5 shadow-sm">
              <img src="/assets/casia-logo.webp" alt="CASIA logo" className="h-12 w-auto" data-testid="about-casia-logo" />
            </div>
          </div>
        </div>
      </section>

      {/* One Institution One Flagship Initiative, six levels from brochure page 3 */}
      <section className="bg-navy" aria-label="One institution, one flagship initiative">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <Overline light>Our Structure</Overline>
          <h2 className="mt-3 font-serif text-2xl font-semibold tracking-tight text-white sm:text-3xl lg:text-4xl" data-testid="six-levels-heading">
            One Institution. <span className="text-gold">One Flagship Initiative.</span>
          </h2>
          <ol className="mt-12 space-y-0">
            {SIX_LEVELS.map((l, i) => (
              <li key={l.level} className="relative grid gap-2 pb-10 pl-14 sm:grid-cols-[220px_1fr] sm:gap-8 sm:pl-16" data-testid={`six-level-${i + 1}`}>
                {i < SIX_LEVELS.length - 1 && (
                  <span className="absolute left-[19px] top-10 h-full w-px bg-white/15 sm:left-[23px]" aria-hidden="true" />
                )}
                <span className="absolute left-0 top-0 flex h-10 w-10 items-center justify-center rounded-full border border-gold bg-navy font-serif text-lg font-bold text-gold sm:h-12 sm:w-12" aria-hidden="true">
                  {i + 1}
                </span>
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-gold">{l.level}</p>
                  <h3 className="mt-1 text-lg font-semibold text-white">{l.title}</h3>
                </div>
                <p className="text-sm leading-relaxed text-slate-300 sm:text-base">{l.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* What every journey leaves behind */}
      <section className="bg-parchment" aria-label="What every journey leaves behind">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <Overline>Learning Evidence</Overline>
          <h2 className="mt-3 font-serif text-2xl font-semibold tracking-tight text-navy sm:text-3xl lg:text-4xl" data-testid="evidence-heading">
            What Every Journey Leaves Behind
          </h2>
          <p className="mt-4 max-w-3xl text-base leading-relaxed text-slate-600">
            Experience alone is not enough. A journey becomes truly educational when evidence is designed into it. Every
            CASIA Curriculum Journey produces a parallel, defensible evidence trail that creates lasting value for both
            the individual student and the institution.
          </p>
          <div className="mt-10 grid gap-6 lg:grid-cols-2">
            <article className="rounded-lg border border-slate-200 bg-white p-7 shadow-sm" data-testid="evidence-student">
              <div className="flex items-center gap-3">
                <GraduationCap className="h-6 w-6 text-gold" aria-hidden="true" />
                <div>
                  <h3 className="text-xl font-semibold text-navy">For the Student</h3>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">A Personal Evidence Record</p>
                </div>
              </div>
              <ul className="mt-6 space-y-4">
                {EVIDENCE_STUDENT.map((e) => (
                  <li key={e.title} className="border-l-2 border-gold pl-4">
                    <p className="text-sm font-semibold text-navy">{e.title}</p>
                    <p className="text-sm text-slate-600">{e.text}</p>
                  </li>
                ))}
              </ul>
            </article>
            <article className="rounded-lg border border-slate-200 bg-white p-7 shadow-sm" data-testid="evidence-school">
              <div className="flex items-center gap-3">
                <Building2 className="h-6 w-6 text-gold" aria-hidden="true" />
                <div>
                  <h3 className="text-xl font-semibold text-navy">For the School</h3>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">An Institutional Record</p>
                </div>
              </div>
              <ul className="mt-6 space-y-4">
                {EVIDENCE_SCHOOL.map((e) => (
                  <li key={e.title} className="border-l-2 border-navy pl-4">
                    <p className="text-sm font-semibold text-navy">{e.title}</p>
                    <p className="text-sm text-slate-600">{e.text}</p>
                  </li>
                ))}
              </ul>
            </article>
          </div>
        </div>
      </section>

      {/* Image band */}
      <section className="bg-ivory" aria-label="Field learning in practice">
        <div className="mx-auto grid max-w-7xl gap-4 px-4 py-16 sm:grid-cols-3 sm:px-6 lg:px-8">
          <img src={IMAGES.fieldEcology} alt="Students in an outdoor ecology field study" className="h-56 w-full rounded-lg object-cover" />
          <img src={IMAGES.robotics} alt="Students building a robotics prototype" className="h-56 w-full rounded-lg object-cover" />
          <img src={IMAGES.humanities} alt="Students studying cultural heritage architecture" className="h-56 w-full rounded-lg object-cover" />
        </div>
      </section>
    </main>
  );
}
